// SettingsPage.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Settings, ArrowLeft, Search, X } from 'lucide-react';
import CityList from './CityList';
import ServiceCenterList from './ServiceCenterList';
import RoleList from './RoleList';
import UserList from './UserList';
import CustomerList from './CustomerList';
import CustomerTerritoryList from './CustomerTerritoryList';
import ProjectList from './ProjectList';
import CustomerUploadList from './CustomerUploadList';
import DictionaryUploadList from './DictionaryUploadList'; // Новый импорт
import DiagnosticCodeList from './DiagnosticCodeList';
import RepairCodeList from './RepairCodeList';
import ProjectCustomFieldList from './ProjectCustomFieldList';
import CustomStatusIntegrationList from './CustomStatusIntegrationList';
import RepairRequestEquipmentList from './RepairRequestEquipmentList';
import styles from './SettingsPage.module.css';
import commonStyles from '../../styles/common.module.css';

const SettingsPage = ({ embedded = false } = {}) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('cities');
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleLimit, setVisibleLimit] = useState(50);
  const [repairCodeLaunch, setRepairCodeLaunch] = useState(null);
  const contentRef = useRef(null);

  const handleBackToProjects = () => {
    navigate('/select-project');
  };

  useEffect(() => {
    if (activeTab === 'repair-equipment') return undefined;
    const root = contentRef.current;
    if (!root) return undefined;

    const applyFilter = () => {
      const query = searchQuery.trim().toLowerCase();
      const rows = root.querySelectorAll('tbody tr');

      let matchedIndex = 0;
      rows.forEach((row) => {
        const rowText = row.innerText.toLowerCase();
        const matches = !query || rowText.includes(query);
        row.hidden = !matches || matchedIndex >= visibleLimit;
        if (matches) matchedIndex += 1;
      });
    };

    applyFilter();

    const observer = new MutationObserver(applyFilter);
    observer.observe(root, { childList: true, subtree: true, characterData: true });

    return () => observer.disconnect();
  }, [activeTab, searchQuery, visibleLimit]);

  useEffect(() => { setVisibleLimit(50); }, [activeTab, searchQuery]);

  const handleContentScroll = (event) => {
    if (activeTab === 'repair-equipment') return;
    const element = event.target;
    if (!(element instanceof HTMLElement)) return;
    if (element.scrollHeight - element.scrollTop - element.clientHeight < 180) {
      setVisibleLimit(limit => limit + 50);
    }
  };

  const handleAddDictionaryWork = (upload) => {
    const availableProjects = upload.projects || [];
    const currentProjectId = Number(localStorage.getItem('selected_project_id'));
    const project = availableProjects.find(item => item.id === currentProjectId) || availableProjects[0];
    if (!project) return;
    setRepairCodeLaunch({
      projectId: project.id,
      dictionary: { value: upload.id, label: upload.name },
      customers: (upload.customers || []).map(item => ({ value: item.id, label: item.name })),
      nonce: Date.now(),
    });
    setActiveTab('repair-codes');
    setSearchQuery('');
  };

  if (loading) {
    return (
      <div className={commonStyles.loading}>
        <div className={`${commonStyles.spinner} ${commonStyles.spinnerTop}`}></div>
        <div className={`${commonStyles.spinner} ${commonStyles.spinnerBottom}`}></div>
      </div>
    );
  }

  const permissionKeys = new Set(user?.permissions || []);
  const canManageSettings = Array.isArray(user?.permissions)
    ? permissionKeys.has('crm.admin.settings') || permissionKeys.has('crm.admin.override')
    : user?.roles?.some(role => role.name === 'admin');

  if (!user || !canManageSettings) {
    return <div className={commonStyles.error}>Доступ запрещён</div>;
  }

  const tabs = [
    { id: 'cities', label: 'Города', component: <CityList /> },
    { id: 'service-centers', label: 'Сервисные центры', component: <ServiceCenterList /> },
    { id: 'roles', label: 'Роли', component: <RoleList /> },
    { id: 'users', label: 'Пользователи', component: <UserList /> },
    { id: 'customers', label: 'Заказчики', component: <CustomerList /> },
    { id: 'customer-territories', label: 'Территории заказчиков', component: <CustomerTerritoryList /> },
    { id: 'projects', label: 'Проекты', component: <ProjectList /> },
    { id: 'customer-uploads', label: 'Загрузки заказчиков', component: <CustomerUploadList /> },
    { id: 'dictionary-uploads', label: 'Загрузки справочников', component: <DictionaryUploadList onAddWork={handleAddDictionaryWork} /> }, // Новый таб
    { id: 'custom-fields', label: 'Поля проектов', component: <ProjectCustomFieldList /> },
    { id: 'diagnostic-codes', label: 'Коды диагностики', component: <DiagnosticCodeList /> },
    { id: 'repair-codes', label: 'Коды ремонта', component: <RepairCodeList launch={repairCodeLaunch} /> },
    { id: 'status-integrations', label: 'Интеграции статусов', component: <CustomStatusIntegrationList /> },
    { id: 'repair-equipment', label: 'Оборудование заявок', component: <RepairRequestEquipmentList searchQuery={searchQuery} /> },
  ];

  const activeTabLabel = tabs.find(tab => tab.id === activeTab)?.label || 'разделу';

  return (
    <div className={`${styles.settingsPage} ${embedded ? styles.embeddedPage : commonStyles.pageContainer} ${commonStyles.fadeIn}`}>
      {!embedded && (
        <div className={styles.pageHeader}>
          <button
            className={styles.backButton}
            onClick={handleBackToProjects}
          >
            <ArrowLeft size={20} />
            Назад к проектам
          </button>
          <div className={styles.titleGroup}>
            <Settings className={styles.titleIcon} />
            <h1 className={commonStyles.pageTitle}>Настройки</h1>
          </div>
        </div>
      )}
      <div className={styles.tabContainer}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`${styles.tabButton} ${activeTab === tab.id ? styles.active : ''}`}
            onClick={() => {
              setActiveTab(tab.id);
              setSearchQuery('');
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className={styles.searchPanel}>
        <div className={styles.searchBox}>
          <Search size={18} className={styles.searchIcon} />
          <input
            type="text"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={`Поиск по разделу: ${activeTabLabel}`}
            aria-label={`Поиск по разделу ${activeTabLabel}`}
          />
          {searchQuery && (
            <button
              type="button"
              className={styles.searchClearButton}
              onClick={() => setSearchQuery('')}
              title="Очистить поиск"
            >
              <X size={16} />
            </button>
          )}
        </div>
        <span className={styles.searchHint}>
          {activeTab === 'repair-equipment'
            ? 'Поиск выполняется по всей базе, независимо от загруженных страниц'
            : 'Фильтр ищет по всем видимым столбцам текущей таблицы'}
        </span>
      </div>

      <div className={styles.content} ref={contentRef} onScrollCapture={handleContentScroll}>
        {tabs.find(tab => tab.id === activeTab)?.component}
      </div>
    </div>
  );
};

export default SettingsPage;
